import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader, Alert, Button } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
  type DraggableProvidedDragHandleProps,
} from '@hello-pangea/dnd';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconChevronDown,
  IconCheck,
  IconClock,
  IconEye,
  IconEyeOff,
  IconFile,
  IconFileText,
  IconGripVertical,
  IconLink,
  IconPlus,
  IconTrash,
  IconVideo,
  IconX,
} from '@tabler/icons-react';
import {
  useCourse,
  useCreateCourse,
  useUpdateCourse,
  useDeleteCourse,
  useCompetencies,
} from '../../../hooks/useApi';
import { apiFormData } from '../../../utils/api';
import { showToast } from '../../../utils/toast';
import { Breadcrumbs } from '../../../components/ui/Breadcrumbs';
import { Course, CourseModule, CourseQuestion } from '../../../types';

// ────────────────────────────────────────────────────────────────────────────
// Cover gradient presets — UI-only, not persisted to backend yet
// ────────────────────────────────────────────────────────────────────────────
const COVERS: Array<{ id: string; from: string; to: string }> = [
  { id: 'teal', from: '#0A6B5A', to: '#12B886' },
  { id: 'blue', from: '#0B4E72', to: '#228BE6' },
  { id: 'purple', from: '#3D2461', to: '#7048E8' },
  { id: 'orange', from: '#7C2D12', to: '#F08C00' },
  { id: 'red', from: '#7F1D1D', to: '#E03131' },
  { id: 'graphite', from: '#1F2329', to: '#495057' },
];

const NEW_PREFIX = 'new-';
const genId = () => `${NEW_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

// ────────────────────────────────────────────────────────────────────────────
// UI primitives
// ────────────────────────────────────────────────────────────────────────────
const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="ce-eyebrow">{children}</div>
);

const IconBtn: React.FC<{
  onClick?: () => void;
  danger?: boolean;
  ariaLabel?: string;
  children: React.ReactNode;
}> = ({ onClick, danger, ariaLabel, children }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    className={`ce-icon-btn${danger ? ' ce-icon-btn-danger' : ''}`}
  >
    {children}
  </button>
);

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({
  checked,
  onChange,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`ce-toggle${checked ? ' ce-toggle-on' : ''}`}
  >
    <span className="ce-toggle-knob" />
  </button>
);

const NumberStepper: React.FC<{
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}> = ({ value, onChange, min = 0, max = 100, step = 1, suffix }) => (
  <div className="ce-stepper">
    <button type="button" onClick={() => onChange(Math.max(min, value - step))}>
      −
    </button>
    <span className="num ce-stepper-value">
      {value}
      {suffix}
    </span>
    <button type="button" onClick={() => onChange(Math.min(max, value + step))}>
      +
    </button>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// State shape (UI only — backend fields mapped on save)
// ────────────────────────────────────────────────────────────────────────────
interface UiModule {
  _id: string;
  title: string;
  contentType: 'text' | 'video' | 'file';
  textContent?: string;
  videoUrl?: string;
  fileUrl?: string;
  fileName?: string;
  durationMin?: number;
  order: number;
  isExpanded: boolean;
}

interface UiQuestion {
  _id: string;
  text: string;
  type: 'single' | 'multiple';
  options: Array<{ text: string; isCorrect: boolean }>;
  order: number;
  isExpanded: boolean;
}

interface UiState {
  title: string;
  description: string;
  modules: UiModule[];
  quizQuestions: UiQuestion[];
  passingScore: number;
  isPublished: boolean;
  coverId: string;
  // ID компетенций, прокачиваемых курсом. После complete будут предложены
  // на подтверждение.
  competencyIds: string[];
}

const emptyState = (): UiState => ({
  title: '',
  description: '',
  modules: [
    {
      _id: genId(),
      title: '',
      contentType: 'text',
      textContent: '',
      order: 0,
      isExpanded: true,
    },
  ],
  quizQuestions: [],
  passingScore: 70,
  isPublished: false,
  coverId: COVERS[0].id,
  competencyIds: [],
});

const stateFromCourse = (c: Course): UiState => ({
  title: c.title,
  description: c.description || '',
  modules: (c.modules || []).map((m, i) => ({
    _id: m._id,
    title: m.title,
    contentType: m.contentType,
    textContent: m.textContent,
    videoUrl: m.videoUrl,
    fileUrl: m.fileUrl,
    fileName: m.fileName,
    durationMin: m.durationMin,
    order: m.order ?? i,
    isExpanded: false,
  })),
  quizQuestions: (c.quizQuestions || []).map((q, i) => ({
    _id: q._id,
    text: q.text,
    type: q.type,
    options: q.options.map((o) => ({ text: o.text, isCorrect: !!o.isCorrect })),
    order: q.order ?? i,
    isExpanded: false,
  })),
  passingScore: c.passingScore,
  isPublished: c.isPublished,
  coverId: c.coverGradient?.preset || COVERS[0].id,
  competencyIds: (c.competencyIds || []).map((ref) => (typeof ref === 'string' ? ref : ref._id)),
});

// ────────────────────────────────────────────────────────────────────────────
// Module editor
// ────────────────────────────────────────────────────────────────────────────
const ModuleRow: React.FC<{
  mod: UiModule;
  idx: number;
  isCreate: boolean;
  courseId?: string;
  pendingFile?: File;
  onPickPendingFile?: (file: File | null) => void;
  onChange: (patch: Partial<UiModule>) => void;
  onRemove: () => void;
  onUpload?: (file: File) => Promise<void>;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
}> = ({
  mod,
  idx,
  isCreate,
  pendingFile,
  onPickPendingFile,
  onChange,
  onRemove,
  onUpload,
  dragHandleProps,
  isDragging,
}) => {
  const { t } = useTranslation();
  const types: Array<{ v: UiModule['contentType']; label: string; icon: React.ReactNode }> = [
    { v: 'text', label: t('learning.editor.moduleTypeText'), icon: <IconFileText size={14} /> },
    { v: 'video', label: t('learning.editor.moduleTypeVideo'), icon: <IconVideo size={14} /> },
    { v: 'file', label: t('learning.editor.moduleTypeFile'), icon: <IconFile size={14} /> },
  ];

  return (
    <div
      className={`ce-module${mod.isExpanded ? ' ce-module-open' : ''}${isDragging ? ' ce-module-dragging' : ''}`}
    >
      <div
        className="ce-module-head"
        onClick={() => !mod.isExpanded && onChange({ isExpanded: true })}
      >
        <div className="ce-drag" {...(dragHandleProps || {})}>
          <IconGripVertical size={14} />
        </div>
        <div className="ce-num-badge num">{idx}</div>
        <input
          type="text"
          value={mod.title}
          onChange={(e) => onChange({ title: e.target.value })}
          onClick={(e) => e.stopPropagation()}
          placeholder={t('learning.editor.modulePlaceholder')}
          className="ce-row-input"
        />
        <div className="ce-type-tabs" onClick={(e) => e.stopPropagation()}>
          {types.map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => {
                onChange({
                  contentType: t.v,
                  textContent: t.v === 'text' ? (mod.textContent ?? '') : undefined,
                  videoUrl: t.v === 'video' ? (mod.videoUrl ?? '') : undefined,
                  fileUrl: t.v === 'file' ? mod.fileUrl : undefined,
                  fileName: t.v === 'file' ? mod.fileName : undefined,
                });
              }}
              className={`ce-type-tab${mod.contentType === t.v ? ' ce-type-tab-active' : ''}`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <div className="ce-row-actions" onClick={(e) => e.stopPropagation()}>
          <div
            className="ce-duration"
            title={t('learning.editor.moduleDurationTitle')}
            onClick={(e) => e.stopPropagation()}
          >
            <IconClock size={12} />
            <input
              type="number"
              min={0}
              max={600}
              value={mod.durationMin ?? ''}
              onChange={(e) =>
                onChange({
                  durationMin:
                    e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)),
                })
              }
              placeholder="—"
              className="ce-duration-input"
            />
            <span className="ce-duration-suffix">{t('learning.editor.moduleDurationSuffix')}</span>
          </div>
          <IconBtn
            onClick={() => onChange({ isExpanded: !mod.isExpanded })}
            ariaLabel={t('learning.editor.moduleCollapse')}
          >
            <IconChevronDown
              size={14}
              style={{
                transform: mod.isExpanded ? 'rotate(180deg)' : undefined,
                transition: 'transform 120ms',
              }}
            />
          </IconBtn>
          <IconBtn danger onClick={onRemove} ariaLabel={t('learning.editor.moduleRemove')}>
            <IconTrash size={14} />
          </IconBtn>
        </div>
      </div>

      {mod.isExpanded && (
        <div className="ce-module-body">
          {mod.contentType === 'text' && (
            <textarea
              value={mod.textContent || ''}
              onChange={(e) => onChange({ textContent: e.target.value })}
              placeholder={t('learning.editor.textContentPlaceholder')}
              className="ce-textarea"
              style={{ minHeight: 140 }}
            />
          )}
          {mod.contentType === 'video' && (
            <div>
              <div className="ce-url-field">
                <IconVideo size={14} />
                <input
                  type="text"
                  value={mod.videoUrl || ''}
                  onChange={(e) => onChange({ videoUrl: e.target.value })}
                  placeholder={t('learning.editor.videoUrlPlaceholder')}
                />
              </div>
              <div className="ce-hint">{t('learning.editor.videoUrlHint')}</div>
            </div>
          )}
          {mod.contentType === 'file' && (
            <div className="ce-dropzone">
              <div className="ce-dropzone-icon">
                <IconFile size={14} />
              </div>
              <div className="ce-dropzone-text">
                {isCreate && pendingFile ? (
                  <>
                    <div className="ce-dropzone-name">{pendingFile.name}</div>
                    <div className="ce-hint" style={{ marginTop: 2 }}>
                      {t('learning.editor.fileDropPending')}
                    </div>
                  </>
                ) : mod.fileName ? (
                  <>
                    <div className="ce-dropzone-name">{mod.fileName}</div>
                    <div className="ce-hint" style={{ marginTop: 2 }}>
                      {t('learning.editor.fileDropUploaded')}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="ce-dropzone-name">
                      {t('learning.editor.fileDropPlaceholder')}
                    </div>
                    <div className="ce-hint" style={{ marginTop: 2 }}>
                      {t('learning.editor.fileDropHint')}
                    </div>
                  </>
                )}
              </div>
              <input
                type="file"
                id={`file-${mod._id}`}
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (isCreate) {
                    onPickPendingFile?.(file);
                    onChange({ fileName: file.name });
                  } else if (onUpload) {
                    await onUpload(file);
                  }
                  e.target.value = '';
                }}
              />
              <Button
                variant="default"
                size="xs"
                onClick={() => document.getElementById(`file-${mod._id}`)?.click()}
              >
                {(isCreate && pendingFile) || mod.fileName
                  ? t('learning.editor.fileReplace')
                  : t('learning.editor.fileUploadBtn')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Question editor
// ────────────────────────────────────────────────────────────────────────────
const QuestionRow: React.FC<{
  q: UiQuestion;
  idx: number;
  onChange: (patch: Partial<UiQuestion>) => void;
  onRemove: () => void;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
}> = ({ q, idx, onChange, onRemove, dragHandleProps, isDragging }) => {
  const { t } = useTranslation();
  const toggleCorrect = (oi: number) => {
    if (q.type === 'single') {
      onChange({ options: q.options.map((o, i) => ({ ...o, isCorrect: i === oi })) });
    } else {
      onChange({
        options: q.options.map((o, i) => (i === oi ? { ...o, isCorrect: !o.isCorrect } : o)),
      });
    }
  };
  const updateOpt = (oi: number, patch: Partial<{ text: string }>) =>
    onChange({ options: q.options.map((o, i) => (i === oi ? { ...o, ...patch } : o)) });
  const addOption = () => onChange({ options: [...q.options, { text: '', isCorrect: false }] });
  const removeOption = (oi: number) => onChange({ options: q.options.filter((_, i) => i !== oi) });

  return (
    <div className={`ce-question${isDragging ? ' ce-question-dragging' : ''}`}>
      <div className="ce-question-head">
        <div className="ce-drag" {...(dragHandleProps || {})}>
          <IconGripVertical size={14} />
        </div>
        <div className="ce-num-badge num">{idx}</div>
        <input
          type="text"
          value={q.text}
          onChange={(e) => onChange({ text: e.target.value })}
          placeholder={t('learning.editor.questionPlaceholder')}
          className="ce-row-input"
        />
        <span className="ce-pill ce-pill-ghost">
          {q.type === 'single'
            ? t('learning.editor.questionPillSingle')
            : t('learning.editor.questionPillMulti')}
        </span>
        <div className="ce-row-actions">
          <IconBtn
            onClick={() => onChange({ isExpanded: !q.isExpanded })}
            ariaLabel={t('learning.editor.moduleCollapse')}
          >
            <IconChevronDown
              size={14}
              style={{
                transform: q.isExpanded ? 'rotate(180deg)' : undefined,
                transition: 'transform 120ms',
              }}
            />
          </IconBtn>
          <IconBtn danger onClick={onRemove} ariaLabel={t('learning.editor.questionRemove')}>
            <IconTrash size={14} />
          </IconBtn>
        </div>
      </div>

      {q.isExpanded && (
        <div className="ce-question-body">
          <div className="ce-question-typebar">
            <Eyebrow>{t('learning.editor.questionTypeEyebrow')}</Eyebrow>
            <div className="ce-mini-tabs">
              {(
                [
                  { v: 'single', label: t('learning.editor.questionTypeSingle') },
                  { v: 'multiple', label: t('learning.editor.questionTypeMulti') },
                ] as Array<{ v: UiQuestion['type']; label: string }>
              ).map((tab) => (
                <button
                  key={tab.v}
                  type="button"
                  onClick={() => {
                    if (tab.v === 'single' && q.type !== 'single') {
                      const firstCorrect = q.options.findIndex((o) => o.isCorrect);
                      onChange({
                        type: 'single',
                        options: q.options.map((o, i) => ({
                          ...o,
                          isCorrect: i === Math.max(firstCorrect, 0),
                        })),
                      });
                    } else {
                      onChange({ type: tab.v });
                    }
                  }}
                  className={`ce-mini-tab${q.type === tab.v ? ' ce-mini-tab-active' : ''}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="ce-options">
            {q.options.map((opt, oi) => (
              <div key={oi} className="ce-option-row">
                <button
                  type="button"
                  onClick={() => toggleCorrect(oi)}
                  className={`ce-option-mark${opt.isCorrect ? ' ce-option-mark-on' : ''}${
                    q.type === 'single' ? ' ce-option-mark-single' : ''
                  }`}
                  aria-label={
                    opt.isCorrect
                      ? t('learning.editor.optionMarkedCorrect')
                      : t('learning.editor.optionMarkAsCorrect')
                  }
                >
                  {opt.isCorrect && <IconCheck size={12} />}
                </button>
                <input
                  type="text"
                  value={opt.text}
                  onChange={(e) => updateOpt(oi, { text: e.target.value })}
                  placeholder={t('learning.editor.optionPlaceholder', { n: oi + 1 })}
                  className={`ce-option-input${opt.isCorrect ? ' ce-option-input-on' : ''}`}
                />
                <IconBtn
                  danger
                  onClick={() => removeOption(oi)}
                  ariaLabel={t('learning.editor.optionRemove')}
                >
                  <IconTrash size={14} />
                </IconBtn>
              </div>
            ))}
          </div>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={12} />}
            onClick={addOption}
            mt={10}
          >
            {t('learning.editor.addOption')}
          </Button>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Competencies tag input — выбираем из реального каталога
// ────────────────────────────────────────────────────────────────────────────
const CompetenciesInput: React.FC<{
  selectedIds: string[];
  catalog: Array<{ _id: string; title: string }>;
  onChange: (ids: string[]) => void;
}> = ({ selectedIds, catalog, onChange }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const byId = useMemo(() => new Map(catalog.map((c) => [c._id, c])), [catalog]);
  const remove = (id: string) => onChange(selectedIds.filter((x) => x !== id));
  const add = (id: string) => {
    if (selectedIds.includes(id)) return;
    onChange([...selectedIds, id]);
    setQuery('');
  };

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return [];
    return catalog
      .filter((c) => !selectedIds.includes(c._id) && c.title.toLowerCase().includes(q))
      .slice(0, 8);
  }, [catalog, selectedIds, q]);

  const suggestions = useMemo(
    () => catalog.filter((c) => !selectedIds.includes(c._id)).slice(0, 4),
    [catalog, selectedIds],
  );

  return (
    <>
      <div className="ce-skills-box" style={{ position: 'relative' }}>
        {selectedIds.map((id) => {
          const item = byId.get(id);
          const label = item?.title || id;
          return (
            <span key={id} className="ce-skill-tag">
              {label}
              <button
                type="button"
                onClick={() => remove(id)}
                aria-label={t('learning.editor.competencyRemove')}
              >
                <IconX size={10} />
              </button>
            </span>
          );
        })}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !query && selectedIds.length) {
              remove(selectedIds[selectedIds.length - 1]);
            } else if (e.key === 'Enter' && matches.length > 0) {
              e.preventDefault();
              add(matches[0]._id);
            }
          }}
          placeholder={selectedIds.length ? '' : t('learning.editor.competenciesPickerPlaceholder')}
          className="ce-skills-input"
        />
        {matches.length > 0 && (
          <div className="ce-suggest-dropdown">
            {matches.map((m) => (
              <button
                key={m._id}
                type="button"
                onClick={() => add(m._id)}
                className="ce-suggest-item"
              >
                {m.title}
              </button>
            ))}
          </div>
        )}
      </div>
      {suggestions.length > 0 && (
        <div className="ce-skills-suggest">
          <span className="ce-hint">{t('learning.editor.competenciesSuggestPrefix')}</span>
          {suggestions.map((s) => (
            <button
              key={s._id}
              type="button"
              onClick={() => add(s._id)}
              className="ce-skill-suggest"
            >
              + {s.title}
            </button>
          ))}
        </div>
      )}
      {catalog.length === 0 && (
        <div className="ce-hint" style={{ marginTop: 8 }}>
          {t('learning.editor.competenciesEmpty')}
        </div>
      )}
    </>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────
export const CourseEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isCreate = id === 'new';
  const { data: existingCourse, isLoading, error } = useCourse(isCreate ? '' : id || '');
  const { data: competenciesCatalog = [] } = useCompetencies();
  const createCourse = useCreateCourse();
  const updateCourse = useUpdateCourse();
  const deleteCourse = useDeleteCourse();

  const [state, setState] = useState<UiState>(emptyState);
  const [saved, setSavedState] = useState<UiState>(emptyState);
  // Файлы, выбранные в create-режиме до того, как курс реально создан в БД.
  // Ключ — UI-id модуля (`new-...`), который маппится на реальный id после
  // createCourse через позицию в массиве state.modules.
  const [pendingUploads, setPendingUploads] = useState<Record<string, File>>({});

  const setPendingFile = (uiModuleId: string, file: File | null) => {
    setPendingUploads((prev) => {
      const next = { ...prev };
      if (file === null) delete next[uiModuleId];
      else next[uiModuleId] = file;
      return next;
    });
  };

  useEffect(() => {
    if (existingCourse && !isCreate) {
      const next = stateFromCourse(existingCourse);
      setState(next);
      setSavedState(next);
    }
  }, [existingCourse, isCreate]);

  const cover = useMemo(
    () => COVERS.find((c) => c.id === state.coverId) || COVERS[0],
    [state.coverId],
  );

  const firstCompetencyLabel = useMemo(() => {
    const firstId = state.competencyIds[0];
    if (!firstId) return '';
    const match = competenciesCatalog.find((c) => c._id === firstId);
    return match?.title || '';
  }, [state.competencyIds, competenciesCatalog]);

  const set = <K extends keyof UiState>(key: K, value: UiState[K]) =>
    setState((s) => ({ ...s, [key]: value }));

  const hasChanges = isCreate || JSON.stringify(state) !== JSON.stringify(saved);

  // ── Modules ──
  const addModule = () =>
    set('modules', [
      ...state.modules,
      {
        _id: genId(),
        title: '',
        contentType: 'text',
        textContent: '',
        order: state.modules.length,
        isExpanded: true,
      },
    ]);
  const updateModule = (mid: string, patch: Partial<UiModule>) =>
    set(
      'modules',
      state.modules.map((m) => (m._id === mid ? { ...m, ...patch } : m)),
    );
  const removeModule = (mid: string) =>
    set(
      'modules',
      state.modules.filter((m) => m._id !== mid),
    );

  const uploadModuleFile = async (mid: string, file: File) => {
    if (isCreate || !id) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await apiFormData(`/courses/${id}/modules/${mid}/upload`, {
        method: 'POST',
        body: fd,
      });
      const updated = res.modules?.find((m: CourseModule) => m._id === mid);
      updateModule(mid, { fileUrl: updated?.fileUrl, fileName: updated?.fileName || file.name });
    } catch (err) {
      console.error('File upload error:', err);
      showToast({
        title: t('common.error'),
        message: t('learning.saveError'),
        type: 'error',
      });
    }
  };

  // ── Quiz ──
  const addQuestion = () =>
    set('quizQuestions', [
      ...state.quizQuestions,
      {
        _id: genId(),
        text: '',
        type: 'single',
        options: [
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
        ],
        order: state.quizQuestions.length,
        isExpanded: true,
      },
    ]);
  const updateQuestion = (qid: string, patch: Partial<UiQuestion>) =>
    set(
      'quizQuestions',
      state.quizQuestions.map((q) => (q._id === qid ? { ...q, ...patch } : q)),
    );
  const removeQuestion = (qid: string) =>
    set(
      'quizQuestions',
      state.quizQuestions.filter((q) => q._id !== qid),
    );

  // ── Reorder через DnD ──────────────────────────────────────────────────
  // Бэк ожидает массив в правильном порядке; поле `order` пересчитываем по
  // индексу при сохранении (см. handleSave ниже).
  const reorder = <T,>(arr: T[], from: number, to: number): T[] => {
    if (from === to) return arr;
    const copy = arr.slice();
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
  };
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    if (result.type === 'modules') {
      set('modules', reorder(state.modules, result.source.index, result.destination.index));
    } else if (result.type === 'questions') {
      set(
        'quizQuestions',
        reorder(state.quizQuestions, result.source.index, result.destination.index),
      );
    }
  };

  // ── Save / delete ──
  const handleSave = async () => {
    type PartialModule = Partial<CourseModule> & { order: number };
    type PartialQuestion = Partial<CourseQuestion> & { order: number };
    const courseData: Partial<Course> = {
      title: state.title,
      description: state.description,
      coverGradient: { from: cover.from, to: cover.to, preset: cover.id },
      competencyIds: state.competencyIds,
      modules: state.modules.map((m, i): PartialModule => {
        const base: PartialModule = {
          title: m.title,
          order: i,
          contentType: m.contentType,
          textContent: m.contentType === 'text' ? m.textContent : undefined,
          videoUrl: m.contentType === 'video' ? m.videoUrl : undefined,
          fileUrl: m.contentType === 'file' ? m.fileUrl : undefined,
          fileName: m.contentType === 'file' ? m.fileName : undefined,
          durationMin: m.durationMin,
        };
        return m._id.startsWith(NEW_PREFIX) ? base : { ...base, _id: m._id };
      }) as CourseModule[],
      quizQuestions: state.quizQuestions.map((q, i): PartialQuestion => {
        const base: PartialQuestion = {
          text: q.text,
          type: q.type,
          options: q.options.map((o) => ({ text: o.text, isCorrect: !!o.isCorrect })),
          order: i,
        };
        return q._id.startsWith(NEW_PREFIX) ? base : { ...base, _id: q._id };
      }) as CourseQuestion[],
      passingScore: state.passingScore,
      isPublished: state.isPublished,
    };

    try {
      if (isCreate) {
        const created = await createCourse.mutateAsync(courseData);
        // Догружаем файлы, выбранные до сохранения. Мапинг ui→real по индексу
        // в массиве state.modules — порядок сохранён, потому что мы шлём массив
        // как есть и handleSave формирует payload в том же порядке.
        const pendingEntries = Object.entries(pendingUploads);
        if (pendingEntries.length > 0) {
          const createdModules = (created.modules || []) as Array<{ _id?: string }>;
          for (const [uiId, file] of pendingEntries) {
            const idx = state.modules.findIndex((m) => m._id === uiId);
            const realId = idx >= 0 ? createdModules[idx]?._id : undefined;
            if (!realId) continue;
            const fd = new FormData();
            fd.append('file', file);
            try {
              await apiFormData(`/courses/${created._id}/modules/${realId}/upload`, {
                method: 'POST',
                body: fd,
              });
            } catch (err) {
              console.error('post-create upload failed', err);
              showToast({
                title: t('common.error'),
                message: t('learning.saveError'),
                type: 'error',
              });
            }
          }
          setPendingUploads({});
        }
        showToast({
          title: t('learning.courseCreated'),
          message: t('learning.courseCreatedMsg'),
        });
        navigate(`/learning/${created._id}`);
      } else {
        await updateCourse.mutateAsync({ id: id!, data: courseData });
        setSavedState(state);
        showToast({ title: t('learning.courseSaved'), message: t('learning.courseSavedMsg') });
      }
    } catch {
      showToast({ title: t('common.error'), message: t('learning.saveError'), type: 'error' });
    }
  };

  const handleDelete = () =>
    modals.openConfirmModal({
      title: t('learning.deleteCourse'),
      children: <span>{t('learning.deleteConfirm')}</span>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteCourse.mutateAsync(id!);
          navigate('/learning');
        } catch {
          // handled by mutation
        }
      },
    });

  const isSaving = createCourse.isPending || updateCourse.isPending;

  if (!isCreate && isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <Loader size="sm" />
      </div>
    );
  }
  if (!isCreate && error) {
    return (
      <div style={{ padding: 32 }}>
        <Alert icon={<IconAlertCircle size={16} />} title={t('common.error')} color="red">
          {t('messages.errorLoading', { item: t('learning.title') })}
        </Alert>
      </div>
    );
  }

  const author = existingCourse?.authorId?.name || '—';
  const created = existingCourse?.createdAt
    ? new Date(existingCourse.createdAt).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    : '—';
  const updated = existingCourse?.updatedAt
    ? new Date(existingCourse.updatedAt).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

  return (
    <div className="course-editor-page">
      <style>{`
        .course-editor-page {
          --ce-bg: var(--mantine-color-body);
          --ce-surface: var(--mantine-color-body);
          --ce-surface-2: var(--mantine-color-default-hover);
          --ce-line: var(--mantine-color-default-border);
          --ce-line-strong: var(--mantine-color-default-border);
          --ce-ink: var(--mantine-color-text);
          --ce-ink-2: var(--mantine-color-text);
          --ce-muted: var(--mantine-color-dimmed);
          --ce-soft: var(--mantine-color-placeholder);
          --ce-accent: var(--mantine-primary-color-filled);
          --ce-accent-soft: var(--mantine-primary-color-light);
          --ce-accent-deep: var(--mantine-primary-color-light-color);
          --ce-warn: #F08C00;
          --ce-warn-soft: rgba(240, 140, 0, 0.14);
          --ce-danger: var(--mantine-color-red-6);
          --ce-danger-soft: rgba(224, 49, 49, 0.12);
          --ce-radius: 8px;
          --ce-radius-lg: 12px;
          color: var(--ce-ink);
          padding: 28px 40px 120px;
          max-width: 1280px;
          margin: 0 auto;
        }

        .course-editor-page .num { font-variant-numeric: tabular-nums; }

        .ce-eyebrow { font-size: 11px; color: var(--ce-muted); text-transform: uppercase; letter-spacing: 0.14em; font-weight: 600; }
        .ce-hint { font-size: 11px; color: var(--ce-muted); }

        .ce-bar { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-bottom: 16px; margin-bottom: 16px; border-bottom: 1px solid var(--ce-line); }

        .ce-hero { display: grid; grid-template-columns: minmax(0, 1fr) 200px; gap: 28px; align-items: flex-start; }
        @media (max-width: 900px) { .ce-hero { grid-template-columns: 1fr; } }
        .ce-title-input { width: 100%; padding: 4px 0; border: none; outline: none; background: transparent; color: var(--ce-ink); font-size: 32px; font-weight: 700; letter-spacing: -0.015em; line-height: 1.15; resize: none; font-family: inherit; }
        .ce-desc-block { margin-top: 22px; }
        .ce-desc-label { font-size: 12px; color: var(--ce-muted); font-weight: 500; margin-bottom: 8px; }
        .ce-textarea { width: 100%; padding: 12px 14px; border: 1px solid var(--ce-line-strong); outline: none; background: var(--ce-surface); color: var(--ce-ink); font-size: 14px; line-height: 1.55; resize: vertical; border-radius: var(--ce-radius); font-family: inherit; }
        .ce-textarea:focus { border-color: var(--ce-accent); box-shadow: 0 0 0 3px var(--ce-accent-soft); }

        .ce-cover { width: 100%; aspect-ratio: 4 / 3; border-radius: var(--ce-radius-lg); position: relative; overflow: hidden; border: 1px solid var(--ce-line); box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03); }
        .ce-cover-dots { position: absolute; inset: 0; opacity: 0.22; background-image: radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1px); background-size: 18px 18px; }
        .ce-cover-skill { position: absolute; bottom: 12px; left: 14px; color: #fff; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.14em; opacity: 0.85; }
        .ce-cover-swatches { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
        .ce-cover-swatch { width: 26px; height: 26px; padding: 0; border-radius: 6px; cursor: pointer; border: 1px solid var(--ce-line-strong); }
        .ce-cover-swatch.active { border: 2px solid var(--ce-ink); }

        .ce-grid { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 24px; margin-top: 32px; align-items: flex-start; }
        @media (max-width: 1100px) { .ce-grid { grid-template-columns: 1fr; } }
        .ce-col-main { display: flex; flex-direction: column; gap: 20px; min-width: 0; }
        .ce-rail { position: sticky; top: 16px; display: flex; flex-direction: column; gap: 16px; }

        .ce-card { background: var(--ce-surface); border: 1px solid var(--ce-line); border-radius: var(--ce-radius-lg); overflow: hidden; }
        .ce-card-head { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid var(--ce-line); }
        .ce-card-title { font-size: 18px; font-weight: 600; margin: 0; }
        .ce-card-body { padding: 14px 20px 20px; }


        .ce-empty { padding: 32px 20px; text-align: center; background: var(--ce-surface-2); border: 1px dashed var(--ce-line-strong); border-radius: var(--ce-radius); color: var(--ce-muted); font-size: 13px; line-height: 1.55; }

        .ce-module { border: 1px solid var(--ce-line); border-radius: var(--ce-radius); background: var(--ce-surface); overflow: hidden; }
        .ce-module-open { border-color: var(--ce-line-strong); }
        .ce-module-head { display: grid; grid-template-columns: 24px 32px 1fr auto auto; gap: 12px; align-items: center; padding: 12px 14px; }
        @media (max-width: 700px) { .ce-module-head { grid-template-columns: 24px 32px 1fr; row-gap: 8px; } .ce-module-head > .ce-type-tabs, .ce-module-head > .ce-row-actions { grid-column: 1 / -1; } }
        .ce-drag { display: flex; align-items: center; justify-content: center; color: var(--ce-soft); cursor: grab; }
        .ce-num-badge { width: 28px; height: 28px; border-radius: 6px; background: var(--ce-accent-soft); color: var(--ce-accent-deep); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; }
        .ce-row-input { border: none; outline: none; background: transparent; font-size: 14px; font-weight: 500; color: var(--ce-ink); width: 100%; font-family: inherit; }
        .ce-row-input::placeholder { color: var(--ce-soft); }
        .ce-row-actions { display: flex; align-items: center; gap: 4px; }
        .ce-module-body { padding: 4px 14px 14px 70px; }
        @media (max-width: 700px) { .ce-module-body { padding: 4px 14px 14px; } }

        .ce-type-tabs { display: flex; align-items: center; gap: 4px; padding: 3px; background: var(--ce-surface-2); border-radius: 6px; }
        .ce-type-tab { padding: 5px 10px; font-size: 12px; font-weight: 500; border: none; border-radius: 4px; background: transparent; color: var(--ce-muted); cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
        .ce-type-tab-active { background: var(--ce-surface); color: var(--ce-ink); box-shadow: 0 1px 2px rgba(0,0,0,0.06); }

        .ce-icon-btn { width: 30px; height: 30px; border: none; background: transparent; color: var(--ce-ink-2); cursor: pointer; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; }
        .ce-icon-btn:hover { background: var(--ce-surface-2); }
        .ce-icon-btn-danger:hover { background: var(--ce-danger-soft); color: var(--ce-danger); }

        .ce-url-field { display: flex; align-items: center; gap: 10px; padding: 0 12px; height: 42px; background: var(--ce-surface); border: 1px solid var(--ce-line-strong); border-radius: var(--ce-radius); color: var(--ce-muted); }
        .ce-url-field:focus-within { border-color: var(--ce-accent); box-shadow: 0 0 0 3px var(--ce-accent-soft); }
        .ce-url-field input { flex: 1; border: none; outline: none; background: transparent; color: var(--ce-ink); font-size: 13px; font-family: inherit; }

        .ce-dropzone { display: flex; align-items: center; gap: 14px; padding: 14px 16px; border: 1px dashed var(--ce-line-strong); background: var(--ce-surface-2); border-radius: var(--ce-radius); }
        .ce-dropzone-icon { width: 36px; height: 36px; border-radius: 8px; background: var(--ce-surface); color: var(--ce-accent-deep); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .ce-dropzone-text { flex: 1; min-width: 0; }
        .ce-dropzone-name { font-size: 13px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--ce-ink); }

        .ce-question { border: 1px solid var(--ce-line-strong); border-radius: var(--ce-radius); background: var(--ce-surface); overflow: hidden; }
        .ce-question-head { display: grid; grid-template-columns: 24px 32px 1fr auto auto; gap: 12px; align-items: center; padding: 12px 14px; }
        .ce-question-body { padding: 14px 14px 14px 70px; border-top: 1px solid var(--ce-line); }
        .ce-module-dragging, .ce-question-dragging { box-shadow: 0 10px 24px rgba(0,0,0,0.18); border-color: var(--ce-accent); }
        .ce-dnd-ghost { background: var(--ce-accent-soft); border: 1px dashed var(--ce-accent); border-radius: var(--ce-radius); }
        .ce-question-typebar { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
        .ce-mini-tabs { display: inline-flex; padding: 3px; background: var(--ce-surface-2); border-radius: 6px; }
        .ce-mini-tab { padding: 4px 10px; font-size: 12px; font-weight: 500; border: none; border-radius: 4px; background: transparent; color: var(--ce-muted); cursor: pointer; }
        .ce-mini-tab-active { background: var(--ce-surface); color: var(--ce-ink); box-shadow: 0 1px 2px rgba(0,0,0,0.06); }

        .ce-options { display: flex; flex-direction: column; gap: 6px; }
        .ce-option-row { display: grid; grid-template-columns: 22px 1fr 28px; gap: 10px; align-items: center; }
        .ce-option-mark { width: 22px; height: 22px; border-radius: 5px; background: transparent; border: 1.5px solid var(--ce-line-strong); color: #fff; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; padding: 0; }
        .ce-option-mark-single { border-radius: 999px; }
        .ce-option-mark-on { background: var(--ce-accent); border-color: var(--ce-accent); }
        .ce-option-input { height: 36px; padding: 0 12px; border: 1px solid var(--ce-line); background: var(--ce-surface); color: var(--ce-ink); border-radius: var(--ce-radius); font-size: 13px; outline: none; width: 100%; font-family: inherit; }
        .ce-option-input-on { background: var(--ce-accent-soft); color: var(--ce-accent-deep); }

        .ce-pill { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 999px; line-height: 1; }
        .ce-pill-accent { background: var(--ce-accent-soft); color: var(--ce-accent-deep); }
        .ce-pill-warn { background: var(--ce-warn-soft); color: var(--ce-warn); }
        .ce-pill-ghost { background: transparent; color: var(--ce-muted); border: 1px solid var(--ce-line); }

        .ce-publish-row { display: flex; align-items: center; gap: 14px; margin-top: 12px; padding: 12px 14px; background: var(--ce-warn-soft); border-radius: var(--ce-radius); }
        .ce-publish-row.on { background: var(--ce-accent-soft); }
        .ce-publish-icon { width: 32px; height: 32px; border-radius: 8px; background: var(--ce-surface); color: var(--ce-warn); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .ce-publish-row.on .ce-publish-icon { color: var(--ce-accent-deep); }
        .ce-publish-title { font-size: 13px; font-weight: 600; }
        .ce-publish-sub { font-size: 11px; color: var(--ce-warn); }
        .ce-publish-row.on .ce-publish-sub { color: var(--ce-accent-deep); }

        .ce-toggle { width: 38px; height: 22px; position: relative; background: var(--ce-line-strong); border: none; border-radius: 999px; cursor: pointer; flex-shrink: 0; padding: 0; }
        .ce-toggle-on { background: var(--ce-accent); }
        .ce-toggle-knob { position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 999px; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,0.2); transition: left 160ms cubic-bezier(.2,.8,.2,1); }
        .ce-toggle-on .ce-toggle-knob { left: 18px; }

        .ce-meta-row { display: flex; justify-content: space-between; gap: 8px; }
        .ce-meta-key { font-size: 12px; color: var(--ce-muted); }
        .ce-meta-val { font-size: 12px; font-weight: 500; color: var(--ce-ink-2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px; }

        .ce-skills-box { display: flex; flex-wrap: wrap; gap: 6px; min-height: 40px; padding: 6px 8px; border: 1px solid var(--ce-line-strong); border-radius: var(--ce-radius); background: var(--ce-surface); align-items: center; }
        .ce-skill-tag { display: inline-flex; align-items: center; gap: 6px; padding: 3px 4px 3px 10px; background: var(--ce-accent-soft); color: var(--ce-accent-deep); border-radius: 999px; font-size: 12px; font-weight: 500; }
        .ce-skill-tag button { border: none; background: transparent; color: currentColor; cursor: pointer; padding: 2px; display: inline-flex; }
        .ce-skills-input { flex: 1; min-width: 120px; border: none; outline: none; font-size: 13px; color: var(--ce-ink); padding: 4px 0; background: transparent; font-family: inherit; }
        .ce-skills-suggest { margin-top: 10px; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
        .ce-skill-suggest { padding: 4px 10px; font-size: 11px; font-weight: 500; border: 1px solid var(--ce-line); background: var(--ce-surface); color: var(--ce-muted); border-radius: 999px; cursor: pointer; }

        .ce-suggest-dropdown { position: absolute; top: calc(100% + 4px); left: 0; right: 0; background: var(--ce-surface); border: 1px solid var(--ce-line-strong); border-radius: var(--ce-radius); box-shadow: 0 8px 24px rgba(0,0,0,0.12); padding: 4px; z-index: 5; max-height: 240px; overflow-y: auto; }
        .ce-suggest-item { display: block; width: 100%; padding: 6px 10px; border: none; background: transparent; color: var(--ce-ink); cursor: pointer; font-size: 13px; text-align: left; border-radius: 4px; }
        .ce-suggest-item:hover { background: var(--ce-surface-2); }

        .ce-duration { display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; border: 1px solid var(--ce-line); background: var(--ce-surface); color: var(--ce-muted); border-radius: 6px; font-size: 12px; }
        .ce-duration-input { width: 40px; border: none; outline: none; background: transparent; color: var(--ce-ink); font-size: 12px; font-variant-numeric: tabular-nums; text-align: right; font-family: inherit; -moz-appearance: textfield; }
        .ce-duration-input::-webkit-outer-spin-button, .ce-duration-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .ce-duration-suffix { color: var(--ce-muted); }

        .ce-stepper { display: inline-flex; align-items: center; background: var(--ce-surface); border: 1px solid var(--ce-line-strong); border-radius: var(--ce-radius); overflow: hidden; }
        .ce-stepper button { width: 32px; height: 32px; border: none; background: var(--ce-surface-2); color: var(--ce-ink-2); cursor: pointer; font-size: 14px; }
        .ce-stepper-value { padding: 0 12px; font-size: 13px; font-weight: 600; min-width: 60px; text-align: center; }

        .ce-danger-card { border-color: rgba(224, 49, 49, 0.2); }

        .ce-savebar { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); z-index: 50; display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: #1F2329; color: #F8F9FA; border-radius: 12px; box-shadow: 0 20px 40px -16px rgba(0,0,0,0.35), 0 6px 16px rgba(0,0,0,0.2); min-width: 460px; }
        .ce-savebar button { height: 34px; padding: 0 12px; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
        .ce-savebar .ce-discard { border: 1px solid rgba(255,255,255,0.18); background: transparent; color: inherit; }
        .ce-savebar .ce-save { border: none; background: var(--ce-accent); color: #fff; padding: 0 14px; }
        .ce-savebar .ce-save:disabled { cursor: wait; }

        .ce-preview-cover { position: relative; height: 100px; border-radius: var(--ce-radius); overflow: hidden; margin-bottom: 12px; }
        .ce-preview-dots { position: absolute; inset: 0; opacity: 0.22; background-image: radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1px); background-size: 16px 16px; }
        .ce-preview-skill { position: absolute; bottom: 10px; left: 12px; color: #fff; font-size: 10px; font-weight: 600; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.12em; }
      `}</style>

      <div className="ce-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Breadcrumbs
            items={[
              {
                label: t('learning.editor.breadcrumbLearning'),
                icon: <IconArrowLeft size={14} />,
                to: '/learning',
              },
              ...(!isCreate
                ? [{ label: t('learning.editor.breadcrumbCourse'), to: `/learning/${id}` }]
                : []),
              {
                label: isCreate
                  ? t('learning.editor.breadcrumbCreating')
                  : t('learning.editor.breadcrumbEditing'),
              },
            ]}
          />
          <span
            className={`ce-pill ${state.isPublished ? 'ce-pill-accent' : 'ce-pill-warn'}`}
            style={{ marginLeft: 6 }}
          >
            {state.isPublished ? t('learning.published') : t('learning.draft')}
          </span>
        </div>
        {!isCreate && (
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconEye size={14} />}
            onClick={() => navigate(`/learning/${id}`)}
          >
            {t('learning.editor.preview')}
          </Button>
        )}
      </div>

      <div className="ce-hero">
        <div>
          <Eyebrow>{t('learning.editor.labelTitle')}</Eyebrow>
          <textarea
            rows={2}
            value={state.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder={t('learning.editor.titlePlaceholder')}
            className="ce-title-input"
          />
          <div className="ce-desc-block">
            <div className="ce-desc-label">{t('learning.courseDescription')}</div>
            <textarea
              value={state.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder={t('learning.editor.descriptionPlaceholder')}
              className="ce-textarea"
              style={{ minHeight: 100 }}
            />
          </div>
        </div>
        <div>
          <Eyebrow>{t('learning.editor.labelCover')}</Eyebrow>
          <div
            className="ce-cover"
            style={{
              background: `linear-gradient(135deg, ${cover.from} 0%, ${cover.to} 100%)`,
              marginTop: 10,
            }}
          >
            <div className="ce-cover-dots" />
            <div className="ce-cover-skill">
              {firstCompetencyLabel || t('learning.editor.coverFallbackCategory')}
            </div>
          </div>
          <div className="ce-cover-swatches">
            {COVERS.map((c) => (
              <button
                key={c.id}
                type="button"
                title={c.id}
                onClick={() => set('coverId', c.id)}
                className={`ce-cover-swatch${c.id === state.coverId ? ' active' : ''}`}
                style={{ background: `linear-gradient(135deg, ${c.from} 0%, ${c.to} 100%)` }}
              />
            ))}
          </div>
        </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="ce-grid">
          <div className="ce-col-main">
            {/* Modules */}
            <div className="ce-card">
              <div className="ce-card-head">
                <div>
                  <Eyebrow>{t('learning.editor.programEyebrow')}</Eyebrow>
                  <h2 className="ce-card-title">
                    {t('learning.editor.modulesTitle', { count: state.modules.length })}
                  </h2>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  leftSection={<IconPlus size={14} />}
                  onClick={addModule}
                >
                  {t('learning.addModule')}
                </Button>
              </div>
              <div className="ce-card-body">
                {state.modules.length === 0 ? (
                  <div className="ce-empty">{t('learning.editor.modulesEmpty')}</div>
                ) : (
                  <Droppable droppableId="modules" type="modules">
                    {(dropProvided) => (
                      <div
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}
                        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                      >
                        {state.modules.map((m, i) => (
                          <Draggable key={m._id} draggableId={m._id} index={i}>
                            {(dragProvided, snapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                style={dragProvided.draggableProps.style}
                              >
                                <ModuleRow
                                  mod={m}
                                  idx={i + 1}
                                  isCreate={isCreate}
                                  courseId={id}
                                  pendingFile={pendingUploads[m._id]}
                                  onPickPendingFile={(file) => setPendingFile(m._id, file)}
                                  onChange={(patch) => updateModule(m._id, patch)}
                                  onRemove={() => removeModule(m._id)}
                                  onUpload={(file) => uploadModuleFile(m._id, file)}
                                  dragHandleProps={dragProvided.dragHandleProps}
                                  isDragging={snapshot.isDragging}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {dropProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                )}
              </div>
            </div>

            {/* Quiz */}
            <div className="ce-card">
              <div className="ce-card-head">
                <div>
                  <Eyebrow>{t('learning.editor.quizEyebrow')}</Eyebrow>
                  <h2 className="ce-card-title">
                    {state.quizQuestions.length === 0
                      ? t('learning.editor.quizNoTest')
                      : t('learning.editor.quizQuestionsCount', {
                          count: state.quizQuestions.length,
                        })}
                  </h2>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  leftSection={<IconPlus size={14} />}
                  onClick={addQuestion}
                >
                  {t('learning.addQuestion')}
                </Button>
              </div>
              <div style={{ padding: '0 20px' }}>
                <div
                  className="ce-meta-row"
                  style={{
                    padding: '14px 0',
                    alignItems: 'center',
                    borderBottom:
                      state.quizQuestions.length > 0 ? '1px solid var(--ce-line)' : 'none',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {t('learning.passingScore')}
                    </div>
                    <div className="ce-hint" style={{ marginTop: 2 }}>
                      {t('learning.editor.passingScoreHint')}
                    </div>
                  </div>
                  <NumberStepper
                    value={state.passingScore}
                    onChange={(v) => set('passingScore', v)}
                    min={50}
                    max={100}
                    step={5}
                    suffix="%"
                  />
                </div>
              </div>
              <div className="ce-card-body" style={{ paddingTop: 14 }}>
                {state.quizQuestions.length === 0 ? (
                  <div className="ce-empty">{t('learning.editor.quizEmpty')}</div>
                ) : (
                  <Droppable droppableId="questions" type="questions">
                    {(dropProvided) => (
                      <div
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}
                        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                      >
                        {state.quizQuestions.map((q, i) => (
                          <Draggable key={q._id} draggableId={q._id} index={i}>
                            {(dragProvided, snapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                style={dragProvided.draggableProps.style}
                              >
                                <QuestionRow
                                  q={q}
                                  idx={i + 1}
                                  onChange={(patch) => updateQuestion(q._id, patch)}
                                  onRemove={() => removeQuestion(q._id)}
                                  dragHandleProps={dragProvided.dragHandleProps}
                                  isDragging={snapshot.isDragging}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {dropProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                )}
              </div>
            </div>

            {!isCreate && (
              <div className="ce-card ce-danger-card">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    padding: '20px',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'var(--ce-danger)',
                        marginBottom: 4,
                      }}
                    >
                      {t('learning.deleteCourse')}
                    </div>
                    <div className="ce-hint">{t('learning.editor.dangerDescription')}</div>
                  </div>
                  <Button
                    variant="outline"
                    color="red"
                    size="sm"
                    leftSection={<IconTrash size={14} />}
                    onClick={handleDelete}
                  >
                    {t('common.delete')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="ce-rail">
            {/* Publish card */}
            <div className="ce-card" style={{ padding: 20 }}>
              <Eyebrow>{t('learning.editor.publishEyebrow')}</Eyebrow>
              <div className={`ce-publish-row${state.isPublished ? ' on' : ''}`}>
                <div className="ce-publish-icon">
                  {state.isPublished ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="ce-publish-title">
                    {state.isPublished
                      ? t('learning.editor.publishedTitle')
                      : t('learning.editor.draftTitle')}
                  </div>
                  <div className="ce-publish-sub">
                    {state.isPublished
                      ? t('learning.editor.publishedSub')
                      : t('learning.editor.draftSub')}
                  </div>
                </div>
                <Toggle checked={state.isPublished} onChange={(v) => set('isPublished', v)} />
              </div>

              {!isCreate && (
                <div
                  style={{
                    marginTop: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div className="ce-meta-row">
                    <span className="ce-meta-key">{t('learning.editor.metaSlug')}</span>
                    <span className="ce-meta-val num">
                      <IconLink size={11} style={{ verticalAlign: '-1px' }} /> /learning/
                      {existingCourse?.slug || id}
                    </span>
                  </div>
                  <div className="ce-meta-row">
                    <span className="ce-meta-key">{t('learning.editor.metaAuthor')}</span>
                    <span className="ce-meta-val">{author}</span>
                  </div>
                  <div className="ce-meta-row">
                    <span className="ce-meta-key">{t('learning.editor.metaCreated')}</span>
                    <span className="ce-meta-val">{created}</span>
                  </div>
                  <div className="ce-meta-row">
                    <span className="ce-meta-key">{t('learning.editor.metaUpdated')}</span>
                    <span className="ce-meta-val">{updated}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Competencies card */}
            <div className="ce-card" style={{ padding: 20 }}>
              <Eyebrow>{t('learning.editor.competenciesEyebrow')}</Eyebrow>
              <div className="ce-hint" style={{ marginTop: 4, marginBottom: 12 }}>
                {t('learning.editor.competenciesHint')}
              </div>
              <CompetenciesInput
                selectedIds={state.competencyIds}
                catalog={competenciesCatalog.map((c) => ({ _id: c._id, title: c.title }))}
                onChange={(ids) => set('competencyIds', ids)}
              />
            </div>

            {/* Preview card */}
            <div className="ce-card">
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--ce-line)' }}>
                <Eyebrow>{t('learning.editor.previewEyebrow')}</Eyebrow>
              </div>
              <div style={{ padding: 16 }}>
                <div
                  className="ce-preview-cover"
                  style={{
                    background: `linear-gradient(135deg, ${cover.from} 0%, ${cover.to} 100%)`,
                  }}
                >
                  <div className="ce-preview-dots" />
                  {firstCompetencyLabel && (
                    <div className="ce-preview-skill">{firstCompetencyLabel}</div>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    marginBottom: 6,
                    minHeight: 36,
                  }}
                >
                  {state.title || (
                    <span style={{ color: 'var(--ce-soft)' }}>
                      {t('learning.editor.previewTitlePlaceholder')}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--ce-muted)',
                    lineHeight: 1.5,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    marginBottom: 10,
                    minHeight: 36,
                  }}
                >
                  {state.description || (
                    <span style={{ color: 'var(--ce-soft)' }}>
                      {t('learning.editor.previewDescriptionPlaceholder')}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 11,
                    color: 'var(--ce-muted)',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <IconClock size={11} />{' '}
                    <span className="num">
                      {state.modules.length * 15} {t('learning.editor.previewMin')}
                    </span>
                  </span>
                  <span
                    style={{ width: 3, height: 3, background: 'var(--ce-soft)', borderRadius: 999 }}
                  />
                  <span>
                    <span className="num">{state.modules.length}</span>{' '}
                    {t('learning.editor.previewModulesShort')}
                  </span>
                  {state.quizQuestions.length > 0 && (
                    <>
                      <span
                        style={{
                          width: 3,
                          height: 3,
                          background: 'var(--ce-soft)',
                          borderRadius: 999,
                        }}
                      />
                      <span>
                        <span className="num">{state.quizQuestions.length}</span>{' '}
                        {t('learning.editor.previewQuiz')}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DragDropContext>

      {hasChanges && (
        <div className="ce-savebar">
          <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--ce-warn)' }} />
          <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>
            {isCreate ? t('learning.editor.savebarCreating') : t('learning.editor.savebarUnsaved')}
            {isCreate && Object.keys(pendingUploads).length > 0 && (
              <span style={{ color: 'var(--ce-accent-deep)', marginLeft: 6 }}>
                {t('learning.editor.savebarPendingFiles', {
                  count: Object.keys(pendingUploads).length,
                })}
              </span>
            )}
          </span>
          {!isCreate && (
            <button type="button" className="ce-discard" onClick={() => setState(saved)}>
              {t('learning.editor.savebarDiscard')}
            </button>
          )}
          <button
            type="button"
            className="ce-save"
            onClick={handleSave}
            disabled={isSaving || !state.title.trim()}
          >
            {isSaving ? (
              t('learning.editor.savebarSaving')
            ) : isCreate ? (
              t('learning.createCourse')
            ) : (
              <>
                <IconCheck size={14} /> {t('common.save')}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
